/**
 * Lunar Calendar Utilities for Vietnamese Calendar (Frontend)
 * Provides conversion between solar and lunar dates
 */

import { Lunar, Solar } from 'lunar-javascript';

/**
 * Convert Gregorian (solar) date to Vietnamese lunar date
 * @param {Date} solarDate - JavaScript Date object
 * @returns {Object} Lunar date object with day, month, year, leapMonth
 */
export function solarToLunar(solarDate) {
  try {
    // Validate input
    if (!solarDate || !(solarDate instanceof Date) || isNaN(solarDate.getTime())) {
      console.warn('Invalid date provided to solarToLunar:', solarDate);
      return {
        day: 0,
        month: 0,
        year: 0,
        leapMonth: false,
        isLunarEvent: false
      };
    }

    // Use the correct API from lunar-javascript
    const year = solarDate.getFullYear();
    const month = solarDate.getMonth() + 1; // JavaScript months are 0-indexed
    const day = solarDate.getDate();

    const solar = Solar.fromYmd(year, month, day);
    const lunar = solar.getLunar();

    // Get leap month status - it's a property, not a method
    const isLeapMonth = typeof lunar.isLeap === 'function' ? lunar.isLeap() : !!lunar.isLeap;

    return {
      day: lunar.getDay(),
      month: lunar.getMonth(),
      year: lunar.getYear(),
      leapMonth: isLeapMonth,
      isLunarEvent: true
    };
  } catch (error) {
    console.error('Error converting solar to lunar:', error, 'Date:', solarDate);
    return {
      day: 0,
      month: 0,
      year: 0,
      leapMonth: false,
      isLunarEvent: false
    };
  }
}

/**
 * Convert lunar date to Gregorian (solar) date
 * @param {number} lunarDay - Day in lunar month (1-30)
 * @param {number} lunarMonth - Lunar month (1-12)
 * @param {number} lunarYear - Lunar year
 * @param {boolean} leapMonth - Whether this is a leap month
 * @returns {Date|null} JavaScript Date object or null if invalid
 */
export function lunarToSolar(lunarDay, lunarMonth, lunarYear, leapMonth = false) {
  try {
    const lunar = Lunar.fromYmd(lunarYear, lunarMonth, lunarDay);
    if (leapMonth) {
      lunar.setLeap(true);
    }

    const solar = lunar.getSolar();
    return new Date(solar.getYear(), solar.getMonth() - 1, solar.getDay());
  } catch (error) {
    console.error('Error converting lunar to solar:', error);
    return null;
  }
}

/**
 * Get formatted lunar date display for a solar date
 * @param {Date} solarDate - JavaScript Date object
 * @param {string} format - "short" or "long"
 * @returns {string} Formatted lunar date string
 */
export function getLunarDateDisplay(solarDate, format = 'short') {
  const lunar = solarToLunar(solarDate);

  if (!lunar.isLunarEvent) {
    return '';
  }

  const leapIndicator = lunar.leapMonth ? '*' : '';

  if (format === 'short') {
    return `${lunar.day}/${lunar.month}${leapIndicator}`;
  } else if (format === 'long') {
    const leapText = lunar.leapMonth ? ' nhuận' : '';
    return `Ngày ${lunar.day} tháng ${lunar.month}${leapText} Âm lịch`;
  }

  return `${lunar.day}/${lunar.month}`;
}

/**
 * Vietnamese holidays definition
 */
const VIETNAMESE_HOLIDAYS = {
  '1/1': {
    name: 'Tết Nguyên Đán',
    nameEn: 'Lunar New Year',
    priority: 'urgent',
    significance: 'major',
    color: '#dc2626',
    durationDays: 3
  },
  '2/1': {
    name: 'Tết Nguyên Đán (Ngày 2)',
    nameEn: 'Lunar New Year (Day 2)',
    priority: 'urgent',
    significance: 'major',
    color: '#dc2626',
    durationDays: 1
  },
  '3/1': {
    name: 'Tết Nguyên Đán (Ngày 3)',
    nameEn: 'Lunar New Year (Day 3)',
    priority: 'urgent',
    significance: 'major',
    color: '#dc2626',
    durationDays: 1
  },
  '15/1': {
    name: 'Tết Nguyên Tiêu',
    nameEn: 'Lantern Festival',
    priority: 'medium',
    significance: 'traditional',
    color: '#f59e0b',
    durationDays: 1
  },
  '3/3': {
    name: 'Lễ Hàn Thực',
    nameEn: 'Cold Food Festival',
    priority: 'low',
    significance: 'traditional',
    color: '#f59e0b',
    durationDays: 1
  },
  '10/3': {
    name: 'Giỗ Tổ Hùng Vương',
    nameEn: "Hung Kings' Festival",
    priority: 'high',
    significance: 'major',
    color: '#dc2626',
    durationDays: 1
  },
  '5/5': {
    name: 'Tết Đoan Ngọ',
    nameEn: 'Double Fifth Festival',
    priority: 'medium',
    significance: 'traditional',
    color: '#f59e0b',
    durationDays: 1
  },
  '15/7': {
    name: 'Vu Lan',
    nameEn: 'Ullambana / Hungry Ghost Festival',
    priority: 'high',
    significance: 'major',
    color: '#8b5cf6',
    durationDays: 1
  },
  '15/8': {
    name: 'Tết Trung Thu',
    nameEn: 'Mid-Autumn Festival',
    priority: 'high',
    significance: 'major',
    color: '#f59e0b',
    durationDays: 1
  },
  '9/9': {
    name: 'Tết Trùng Cửu',
    nameEn: 'Double Ninth Festival',
    priority: 'low',
    significance: 'traditional',
    color: '#f59e0b',
    durationDays: 1
  }
};

/**
 * Check if a solar date is a Vietnamese lunar holiday
 * @param {Date} solarDate - JavaScript Date object
 * @returns {Object|null} Holiday information or null if not a holiday
 */
export function isVietnameseHoliday(solarDate) {
  const lunar = solarToLunar(solarDate);

  if (!lunar.isLunarEvent) {
    return null;
  }

  const holidayKey = `${lunar.day}/${lunar.month}`;

  // Check for specific holidays first
  if (VIETNAMESE_HOLIDAYS[holidayKey]) {
    return {
      ...VIETNAMESE_HOLIDAYS[holidayKey],
      lunarDate: lunar,
      solarDate: solarDate
    };
  }

  // Check for monthly observance days (1st and 15th of every lunar month)
  if (lunar.day === 1) {
    return {
      name: 'Mùng 1 Âm Lịch',
      nameEn: '1st Day of Lunar Month',
      priority: 'medium',
      significance: 'observance',
      color: '#3b82f6',
      durationDays: 1,
      lunarDate: lunar,
      solarDate: solarDate
    };
  }

  if (lunar.day === 15) {
    return {
      name: 'Rằm Âm Lịch',
      nameEn: 'Full Moon Day (15th)',
      priority: 'medium',
      significance: 'observance',
      color: '#8b5cf6',
      durationDays: 1,
      lunarDate: lunar,
      solarDate: solarDate
    };
  }

  return null;
}

/**
 * Pick the holiday name in the active language. EN falls back to VN if no
 * nameEn is set. Used by calendar UI to render holiday badges.
 *
 * @param {object|null} holiday - Holiday object from isVietnameseHoliday()
 * @param {string} lang - 'vi' or 'en'
 * @returns {string} Display name
 */
export function getHolidayName(holiday, lang) {
  if (!holiday) return '';
  return lang === 'en' ? (holiday.nameEn || holiday.name) : holiday.name;
}

/**
 * Get the Vietnamese zodiac animal for a lunar year
 * @param {number} lunarYear - Lunar year
 * @returns {string} Zodiac animal name
 */
export function getLunarYearName(lunarYear) {
  const zodiacAnimals = [
    'Tý (Rat)', 'Sửu (Ox)', 'Dần (Tiger)', 'Mão (Cat)',
    'Thìn (Dragon)', 'Tị (Snake)', 'Ngọ (Horse)', 'Mùi (Goat)',
    'Thân (Monkey)', 'Dậu (Rooster)', 'Tuất (Dog)', 'Hợi (Pig)'
  ];

  const index = (lunarYear - 4) % 12;
  return zodiacAnimals[index];
}

/**
 * Get color for lunar event type
 * @param {string} eventType - Event type or holiday type
 * @returns {string} Hex color code
 */
export function getLunarEventColor(eventType) {
  const colorMap = {
    'tet_nguyen_dan': '#dc2626',  // Red for Tết
    'tet_trung_thu': '#f59e0b',   // Orange/Gold for Mid-Autumn
    'vu_lan': '#8b5cf6',          // Purple for Vu Lan
    'gio_to_hung_vuong': '#dc2626',  // Red for Hung Kings
    'lunar_holiday': '#f59e0b',   // Gold for general lunar holidays
    'default': '#f59e0b'
  };

  return colorMap[eventType] || colorMap['default'];
}

/**
 * Classify a date as Hoàng Đạo (good day, "yellow path") or Hắc Đạo (bad day,
 * "black path"). Uses lunar-javascript's day TianShen classification, which
 * maps the day's Earthly Branch against the month's Earthly Branch.
 *
 * @param {Date} solarDate
 * @returns {'hoang_dao'|'hac_dao'|null}
 */
export function getDayLuck(solarDate) {
  try {
    if (!(solarDate instanceof Date) || isNaN(solarDate.getTime())) return null;
    const solar = Solar.fromYmd(solarDate.getFullYear(), solarDate.getMonth() + 1, solarDate.getDate());
    const lunar = solar.getLunar();
    // getDayTianShenType() returns "黄道" or "黑道" — the actual hoàng/hắc đạo
    // classification. (getDayTianShenLuck() returns 吉/凶 which is the related
    // luck label but doesn't map 1:1 to the path color.)
    const type = lunar.getDayTianShenType();
    if (type === '黄道') return 'hoang_dao';
    if (type === '黑道') return 'hac_dao';
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Legacy alias — kept for any existing callers. Hoàng Đạo days are
 * traditionally considered auspicious in Vietnamese astrology.
 */
export function isAuspiciousDay(solarDate) {
  return getDayLuck(solarDate) === 'hoang_dao';
}

/**
 * Format lunar date for display in events
 * @param {Object} lunarDate - Lunar date object from metadata
 * @returns {string} Formatted string
 */
export function formatLunarMetadata(lunarDate) {
  if (!lunarDate || !lunarDate.is_lunar_event) {
    return '';
  }

  const leapIndicator = lunarDate.leap_month ? '*' : '';
  return `${lunarDate.day}/${lunarDate.month}${leapIndicator} Âm`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vietnamese romanization of Sino-Chinese astrology terms.
// The lunar-javascript library returns Chinese characters; Vietnamese feng-shui
// uses the same concepts but with Sino-Vietnamese spellings (e.g. 黄道 → Hoàng
// Đạo, 子 → Tý, 寅 → Dần).
// ─────────────────────────────────────────────────────────────────────────────

const HEAVENLY_STEMS = {
  '甲': 'Giáp', '乙': 'Ất', '丙': 'Bính', '丁': 'Đinh',  '戊': 'Mậu',
  '己': 'Kỷ',   '庚': 'Canh','辛': 'Tân',  '壬': 'Nhâm', '癸': 'Quý',
};
const EARTHLY_BRANCHES = {
  '子': 'Tý',  '丑': 'Sửu', '寅': 'Dần', '卯': 'Mão',
  '辰': 'Thìn','巳': 'Tỵ',  '午': 'Ngọ', '未': 'Mùi',
  '申': 'Thân','酉': 'Dậu', '戌': 'Tuất','亥': 'Hợi',
};
const DIRECTIONS = {
  '正东': 'Chính Đông', '正南': 'Chính Nam', '正西': 'Chính Tây', '正北': 'Chính Bắc',
  '东北': 'Đông Bắc',   '东南': 'Đông Nam',  '西南': 'Tây Nam',   '西北': 'Tây Bắc',
  '东':   'Đông',       '南':   'Nam',       '西':   'Tây',       '北':   'Bắc',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sino-Vietnamese translation tables. The lunar-javascript library returns
// Chinese characters for activities (Yi/Ji), stars (JiShen/XiongSha), 28
// mansions (Xiu), and Peng Zu phrases. Vietnamese feng-shui sites use the
// same concepts with Sino-Vietnamese vocabulary (e.g. 祭祀 → Cúng tế).
//
// The maps below cover the ~80 most common values returned by lunar-javascript
// for daily Yi/Ji. Anything missing falls back to the original Chinese so the
// user can still see the data — they'll just get a chip with Hanzi instead of
// a Vietnamese label. New entries can be added without touching call sites.
// ─────────────────────────────────────────────────────────────────────────────

const YI_JI_VI = {
  // — Lễ nghi / cúng bái —
  '祭祀': 'Cúng tế',
  '祈福': 'Cầu phúc',
  '求嗣': 'Cầu con',
  '斋醮': 'Lễ trai chay',
  '开光': 'Khai quang',
  '塑绘': 'Đắp vẽ tượng',
  '上香': 'Thắp hương',
  '酬神': 'Tạ thần',
  '谢土': 'Tạ thổ',
  '安香': 'An hương',
  '安神': 'An vị thần',
  '出火': 'Xuất hỏa',
  '入殓': 'Liệm xác',
  '移柩': 'Di linh cữu',
  '启钻': 'Khởi quan (cải táng)',
  '安葬': 'An táng',
  '除服': 'Trừ phục',
  '成服': 'Thành phục',

  // — Hôn nhân / gia đình —
  '嫁娶': 'Cưới hỏi',
  '订盟': 'Đính ước',
  '纳采': 'Nạp thái',
  '问名': 'Vấn danh',
  '会亲友': 'Gặp thân hữu',
  '冠笄': 'Đội mũ cài trâm',

  // — Nhà cửa / xây dựng —
  '动土': 'Động thổ',
  '破土': 'Phá thổ',
  '修造': 'Tu sửa nhà',
  '起基': 'Khởi công móng',
  '定磉': 'Định móng',
  '定礎': 'Định nền',
  '上梁': 'Thượng lương',
  '竖柱': 'Dựng cột',
  '盖屋': 'Lợp nhà',
  '盖屋合脊': 'Lợp nóc nhà',
  '安门': 'Đặt cửa',
  '安床': 'An giường',
  '作灶': 'Đặt bếp',
  '修门': 'Sửa cửa',
  '修饰垣墙': 'Tu bổ tường',
  '平治道涂': 'Sửa đường',
  '补垣': 'Bít tường',
  '塞穴': 'Lấp hang',
  '拆卸': 'Phá dỡ',
  '伐木': 'Đốn cây',
  '入宅': 'Nhập trạch',
  '移徙': 'Di chuyển nhà',
  '搬家': 'Chuyển nhà',

  // — Kinh doanh / tài chính —
  '开市': 'Khai trương',
  '立券': 'Lập khế ước',
  '交易': 'Giao dịch',
  '纳财': 'Nạp tài',
  '出货财': 'Xuất tiền hàng',
  '开仓': 'Mở kho',
  '开仓库': 'Mở kho',
  '开池': 'Mở ao',
  '开渠': 'Đào mương',
  '放水': 'Tháo nước',
  '造仓': 'Đắp kho',
  '造船': 'Đóng thuyền',
  '造畜稠': 'Làm chuồng',

  // — Đi lại / giao thiệp —
  '出行': 'Xuất hành',
  '上官赴任': 'Nhậm chức',
  '临政亲民': 'Tiếp dân',
  '入学': 'Nhập học',
  '求医': 'Khám chữa bệnh',
  '求医疗病': 'Chữa bệnh',
  '疗病': 'Trị bệnh',
  '解除': 'Giải trừ',
  '沐浴': 'Tẩy uế',
  '剃头': 'Cắt tóc',
  '整手足甲': 'Cắt móng',
  '裁衣': 'May áo',
  '冠带': 'Mặc lễ phục',

  // — Nông nghiệp —
  '栽种': 'Trồng trọt',
  '收养': 'Nhận con nuôi',
  '纳畜': 'Nuôi súc vật',
  '牧养': 'Chăn nuôi',
  '捕捉': 'Săn bắt',
  '畋猎': 'Đi săn',
  '取渔': 'Đánh cá',
  '结网': 'Đan lưới',
  '扫舍': 'Quét nhà',
  '扫舍宇': 'Quét dọn',

  // — Đại kỵ —
  '诸事不宜': 'Mọi việc đều không nên',
  '余事勿取': 'Việc khác không nên',
  '诸事少取': 'Hạn chế làm việc lớn',
  '馀事勿取': 'Việc khác không nên',
};

// 28 lunar mansions (Nhị thập bát tú) — Sino-Vietnamese names.
const XIU_VI = {
  '角': 'Giác', '亢': 'Cang', '氐': 'Đê', '房': 'Phòng', '心': 'Tâm', '尾': 'Vĩ', '箕': 'Cơ',
  '斗': 'Đẩu', '牛': 'Ngưu', '女': 'Nữ', '虚': 'Hư', '危': 'Nguy', '室': 'Thất', '壁': 'Bích',
  '奎': 'Khuê', '娄': 'Lâu', '胃': 'Vị', '昴': 'Mão', '毕': 'Tất', '觜': 'Chủy', '参': 'Sâm',
  '井': 'Tỉnh', '鬼': 'Quỷ', '柳': 'Liễu', '星': 'Tinh', '张': 'Trương', '翼': 'Dực', '轸': 'Chẩn',
};

// Brief Vietnamese description of each mansion's general nature.
// Used in lieu of translating the classical Chinese verse (xiuSong).
const XIU_DESC_VI = {
  '角': 'Sao tốt cho khai trương, cưới hỏi, giao thiệp. Kỵ an táng, mai táng.',
  '亢': 'Sao xấu, kỵ khởi sự lớn, cưới hỏi, an táng. Chỉ tốt cho việc nhỏ.',
  '氐': 'Sao xấu, kỵ tạo tác, cưới hỏi, đi xa. Dễ gặp tai họa.',
  '房': 'Sao tốt, lợi cho khai trương, hôn nhân, cầu tài, xây dựng.',
  '心': 'Sao xấu, kỵ tạo tác, hôn nhân, an táng. Chỉ tốt cho cúng tế.',
  '尾': 'Sao tốt cho hôn nhân, sửa chữa, đào giếng, mở cửa.',
  '箕': 'Sao tốt cho xây dựng, kinh doanh, mưu cầu tài lộc.',
  '斗': 'Sao tốt cho xây dựng, kinh doanh, lập gia thất.',
  '牛': 'Sao xấu, kỵ cưới hỏi, đào giếng, kết giao bạn bè.',
  '女': 'Sao xấu, kỵ tạo tác, hôn nhân, đi xa.',
  '虚': 'Sao xấu, kỵ tạo tác, kinh doanh, cưới hỏi. Dễ hao tài.',
  '危': 'Sao xấu, kỵ đi xa, leo cao. Tai họa khôn lường.',
  '室': 'Sao tốt cho hôn nhân, xây dựng, kinh doanh, dời nhà.',
  '壁': 'Sao tốt cho hôn nhân, kinh doanh, học hành, cầu công danh.',
  '奎': 'Sao vừa, tốt cho cầu phúc, học hành; kỵ tạo tác, hôn nhân.',
  '娄': 'Sao tốt cho hôn nhân, khai trương, kết giao, dời nhà.',
  '胃': 'Sao tốt cho cưới hỏi, xây dựng, an táng, kinh doanh.',
  '昴': 'Sao xấu, kỵ hôn nhân, kinh doanh, tạo tác. Dễ ly tán.',
  '毕': 'Sao tốt cho cưới hỏi, mua đất, kinh doanh, an táng.',
  '觜': 'Sao xấu, kỵ tạo tác, kinh doanh, hôn nhân. Chỉ tốt việc nhỏ.',
  '参': 'Sao tốt cho sửa nhà, kinh doanh; kỵ an táng, hôn nhân.',
  '井': 'Sao tốt cho kinh doanh, xây dựng, cầu công danh.',
  '鬼': 'Sao xấu, chỉ tốt cho an táng. Kỵ mọi việc khác.',
  '柳': 'Sao xấu, kỵ tạo tác, hôn nhân, kinh doanh, đi xa.',
  '星': 'Sao vừa, tốt cho học hành, cầu công danh; kỵ kiện tụng.',
  '张': 'Sao tốt cho hôn nhân, kinh doanh, sửa nhà, kết giao.',
  '翼': 'Sao xấu, kỵ hôn nhân, kinh doanh, đi xa, kết bạn.',
  '轸': 'Sao tốt cho cưới hỏi, mua đất, kinh doanh, đi xa.',
};

// Zodiac animals — Chinese character → Vietnamese (used by chong / shengxiao)
const ZODIAC_ANIMALS_VI = {
  '鼠': 'Tý',  '牛': 'Sửu', '虎': 'Dần', '兔': 'Mão',
  '龙': 'Thìn','蛇': 'Tỵ',  '马': 'Ngọ', '羊': 'Mùi',
  '猴': 'Thân','鸡': 'Dậu', '狗': 'Tuất','猪': 'Hợi',
};

// Nạp âm — 30 unique five-element names assigned to each Jiazi pair.
const NA_YIN_VI = {
  '海中金': 'Hải Trung Kim',  '炉中火': 'Lư Trung Hỏa',  '大林木': 'Đại Lâm Mộc',
  '路傍土': 'Lộ Bàng Thổ',    '剑锋金': 'Kiếm Phong Kim','山头火': 'Sơn Đầu Hỏa',
  '涧下水': 'Giản Hạ Thủy',   '城头土': 'Thành Đầu Thổ', '白蜡金': 'Bạch Lạp Kim',
  '杨柳木': 'Dương Liễu Mộc', '泉中水': 'Tuyền Trung Thủy','屋上土': 'Ốc Thượng Thổ',
  '霹雳火': 'Tích Lịch Hỏa',  '松柏木': 'Tùng Bách Mộc', '长流水': 'Trường Lưu Thủy',
  '沙中金': 'Sa Trung Kim',   '山下火': 'Sơn Hạ Hỏa',    '平地木': 'Bình Địa Mộc',
  '壁上土': 'Bích Thượng Thổ','金箔金': 'Kim Bạc Kim',   '覆灯火': 'Phú Đăng Hỏa',
  '天河水': 'Thiên Hà Thủy',  '大驿土': 'Đại Trạch Thổ', '钗钏金': 'Thoa Xuyến Kim',
  '桑柘木': 'Tang Đố Mộc',    '大溪水': 'Đại Khê Thủy',  '沙中土': 'Sa Trung Thổ',
  '天上火': 'Thiên Thượng Hỏa','石榴木': 'Thạch Lựu Mộc','大海水': 'Đại Hải Thủy',
};

// Common JiShen (sao tốt) and XiongSha (sao xấu) names.
const STAR_VI = {
  // Sao tốt
  '天德': 'Thiên Đức', '月德': 'Nguyệt Đức', '天德合': 'Thiên Đức hợp', '月德合': 'Nguyệt Đức hợp',
  '天恩': 'Thiên Ân', '天赦': 'Thiên Xá', '天愿': 'Thiên Nguyện', '月恩': 'Nguyệt Ân',
  '四相': 'Tứ Tướng', '时德': 'Thời Đức', '阳德': 'Dương Đức', '阴德': 'Âm Đức',
  '王日': 'Vương Nhật', '官日': 'Quan Nhật', '守日': 'Thủ Nhật', '相日': 'Tướng Nhật',
  '民日': 'Dân Nhật', '青龙': 'Thanh Long', '明堂': 'Minh Đường', '金匮': 'Kim Quỹ',
  '宝光': 'Bảo Quang', '玉堂': 'Ngọc Đường', '司命': 'Tư Mệnh', '三合': 'Tam Hợp',
  '六合': 'Lục Hợp', '五合': 'Ngũ Hợp', '鸣吠': 'Minh Phệ', '不将': 'Bất Tương',
  '益后': 'Ích Hậu', '续世': 'Tục Thế', '驿马': 'Dịch Mã', '天后': 'Thiên Hậu',
  '生气': 'Sinh Khí', '吉期': 'Cát Kỳ', '六仪': 'Lục Nghi', '玉宇': 'Ngọc Vũ',
  '金堂': 'Kim Đường', '敬安': 'Kính An', '普护': 'Phổ Hộ', '福生': 'Phúc Sinh',
  '解神': 'Giải Thần',

  // Sao xấu
  '月破': 'Nguyệt Phá', '大耗': 'Đại Hao', '小耗': 'Tiểu Hao', '灾煞': 'Tai Sát',
  '天火': 'Thiên Hỏa', '月厌': 'Nguyệt Yếm', '地火': 'Địa Hỏa', '四穷': 'Tứ Cùng',
  '九空': 'Cửu Không', '九坎': 'Cửu Khảm', '九焦': 'Cửu Tiêu', '阳错': 'Dương Thác',
  '阴错': 'Âm Thác', '复日': 'Phục Nhật', '重日': 'Trùng Nhật', '游祸': 'Du Họa',
  '天牢': 'Thiên Lao', '白虎': 'Bạch Hổ', '玄武': 'Huyền Vũ', '朱雀': 'Chu Tước',
  '勾陈': 'Câu Trận', '天刑': 'Thiên Hình', '元武': 'Huyền Vũ', '月害': 'Nguyệt Hại',
  '月煞': 'Nguyệt Sát', '月刑': 'Nguyệt Hình', '五虚': 'Ngũ Hư', '土符': 'Thổ Phù',
  '咸池': 'Hàm Trì', '披麻': 'Phi Ma', '五墓': 'Ngũ Mộ', '岁破': 'Tuế Phá',
  '往亡': 'Vãng Vong', '归忌': 'Quy Kỵ', '血支': 'Huyết Chi', '血忌': 'Huyết Kỵ',
  '触水龙': 'Xúc Thủy Long', '土忌': 'Thổ Kỵ',
};

const translate = (s, table) => (s && table[s]) ? table[s] : s;

// Bành Tổ Bách Kỵ Nhật — 10 stem phrases + 12 branch phrases. lunar-javascript
// returns one full Chinese line; we map to the conventional Sino-Vietnamese
// reading plus a brief gloss in parentheses.
const PENG_ZU_VI = {
  // Stems
  '甲不开仓财物耗散': 'Giáp bất khai thương — không nên mở kho, tài vật hao tán.',
  '乙不栽植千株不长': 'Ất bất tài thực — không nên trồng cây, ngàn cây không lớn.',
  '丙不修灶必见灾殃': 'Bính bất tu táo — không nên sửa bếp, ắt gặp tai ương.',
  '丁不剃头头必生疮': 'Đinh bất thế đầu — không nên cắt tóc, đầu sẽ sinh nhọt.',
  '戊不受田田主不祥': 'Mậu bất thụ điền — không nên nhận ruộng, chủ ruộng không lành.',
  '己不破券二比并亡': 'Kỷ bất phá khoán — không nên phá khế ước, hai bên cùng mất.',
  '庚不经络织机虚张': 'Canh bất kinh lạc — không nên dệt cửi, khung dệt trống không.',
  '辛不合酱主人不尝': 'Tân bất hợp tương — không nên làm tương, chủ nhà không được nếm.',
  '壬不汲水更难提防': 'Nhâm bất cấp thủy — không nên kéo nước, càng khó phòng tránh.',
  '癸不词讼理弱敌强': 'Quý bất từ tụng — không nên kiện tụng, lý lẽ yếu kẻ địch mạnh.',
  // Branches
  '子不问卜自惹祸殃': 'Tý bất vấn bốc — không nên xem bói, tự rước họa.',
  '丑不冠带主不还乡': 'Sửu bất quan đới — không nên đội mũ thụ chức, chủ không về quê.',
  '寅不祭祀神鬼不尝': 'Dần bất tế tự — không nên cúng tế, thần quỷ không hưởng.',
  '卯不穿井水泉不香': 'Mão bất xuyên tỉnh — không nên đào giếng, nước không thơm.',
  '辰不哭泣必主重丧': 'Thìn bất khốc khấp — không nên khóc lóc, ắt chủ trọng tang.',
  '巳不远行财物伏藏': 'Tỵ bất viễn hành — không nên đi xa, tài vật ẩn náu.',
  '午不苫盖屋主更张': 'Ngọ bất chiêm cái — không nên lợp nhà, chủ nhà đổi thay.',
  '未不服药毒气入肠': 'Mùi bất phục dược — không nên uống thuốc, độc khí vào ruột.',
  '申不安床鬼祟入房': 'Thân bất an sàng — không nên đặt giường, ma quỷ vào phòng.',
  '酉不会客醉坐颠狂': 'Dậu bất hội khách — không nên tiếp khách, say ngồi điên cuồng.',
  '戌不吃犬作怪上床': 'Tuất bất khất khuyển — không nên ăn thịt chó, ma quái lên giường.',
  '亥不嫁娶不利新郎': 'Hợi bất giá thú — không nên cưới hỏi, không lợi cho chú rể.',
};

// Thập Nhị Kiến Trừ — 12-phase building cycle. Each gives the Vietnamese
// (Sino-Vietnamese) name and a short note on what the day is auspicious for.
const ZHI_XING = {
  '建': { name: 'Kiến',  hint: 'Ngày tốt cho khai trương, cưới hỏi, giao thiệp.' },
  '除': { name: 'Trừ',   hint: 'Tốt cho diệt trừ điều xấu, dọn dẹp, chữa bệnh.' },
  '满': { name: 'Mãn',   hint: 'Tốt cho cúng lễ, an táng; kỵ đào giếng, đắp đập.' },
  '平': { name: 'Bình',  hint: 'Bình ổn, tốt cho việc thường ngày, gặp gỡ.' },
  '定': { name: 'Định',  hint: 'Tốt cho đính hôn, ký kết, an cư.' },
  '执': { name: 'Chấp',  hint: 'Tốt giữ gìn, ổn định công việc cũ; kỵ khởi sự mới.' },
  '破': { name: 'Phá',   hint: 'Xấu, kỵ cưới hỏi, khai trương; tốt cho phá dỡ.' },
  '危': { name: 'Nguy',  hint: 'Hung, kỵ đi xa, làm việc lớn.' },
  '成': { name: 'Thành', hint: 'Tốt cho mọi việc, đặc biệt động thổ, kết hôn.' },
  '收': { name: 'Thu',   hint: 'Tốt cho thu hoạch, đòi nợ, cất giữ tài sản.' },
  '开': { name: 'Khai',  hint: 'Tốt cho khai trương, khởi sự, học hành.' },
  '闭': { name: 'Bế',    hint: 'Kỵ khai trương, xuất hành; tốt cho an táng, đắp đập.' },
};

// Khổng Minh Lục Diệu — 6-day rotation per lunar month. Day 1 → Đại An.
const LUC_DIEU = [
  { name: 'Đại An',    luck: 'good',   note: 'Mọi sự bình an, hanh thông. Tốt cho cầu tài, hôn nhân, đi xa.' },
  { name: 'Lưu Liên',  luck: 'bad',    note: 'Trì trệ, công việc khó thành. Kỵ khởi sự mới, ký kết.' },
  { name: 'Tốc Hỷ',    luck: 'good',   note: 'Niềm vui đến nhanh. Tốt cho cầu tài, gặp gỡ buổi sáng.' },
  { name: 'Xích Khẩu', luck: 'bad',    note: 'Khẩu thiệt, cãi vã. Kỵ tranh chấp, tụng tụng.' },
  { name: 'Tiểu Cát',  luck: 'good',   note: 'May mắn nhỏ, công việc thuận lợi. Tốt cho mọi việc.' },
  { name: 'Không Vong', luck: 'bad',   note: 'Trống rỗng, không kết quả. Kỵ khởi sự, cầu tài, kiện cáo.' },
];

// Heavenly Stem → element (ngũ hành).
const STEM_ELEMENT = {
  '甲': 'Mộc', '乙': 'Mộc', '丙': 'Hỏa', '丁': 'Hỏa',
  '戊': 'Thổ', '己': 'Thổ', '庚': 'Kim', '辛': 'Kim',
  '壬': 'Thủy', '癸': 'Thủy',
};
const BRANCH_ELEMENT = {
  '寅': 'Mộc', '卯': 'Mộc', '巳': 'Hỏa', '午': 'Hỏa',
  '辰': 'Thổ', '戌': 'Thổ', '丑': 'Thổ', '未': 'Thổ',
  '申': 'Kim', '酉': 'Kim', '亥': 'Thủy', '子': 'Thủy',
};

// Five-element generation/overcoming cycle.
const ELEMENT_GENERATES = { 'Mộc': 'Hỏa', 'Hỏa': 'Thổ', 'Thổ': 'Kim', 'Kim': 'Thủy', 'Thủy': 'Mộc' };
const ELEMENT_OVERCOMES = { 'Mộc': 'Thổ', 'Thổ': 'Thủy', 'Thủy': 'Hỏa', 'Hỏa': 'Kim', 'Kim': 'Mộc' };

/** Classify the relationship between a day's Stem (Can) and Branch (Chi). */
function canChiRelation(stemEl, branchEl) {
  if (!stemEl || !branchEl) return null;
  if (stemEl === branchEl)
    return { kind: 'chuyen', label: 'Chuyên nhật', luck: 'good', desc: 'Can Chi cùng hành — ngày tốt, mọi việc hanh thông.' };
  if (ELEMENT_GENERATES[stemEl] === branchEl)
    return { kind: 'nghia', label: 'Nghĩa nhật', luck: 'good', desc: `Can sinh Chi (${stemEl} sinh ${branchEl}) — ngày cát.` };
  if (ELEMENT_GENERATES[branchEl] === stemEl)
    return { kind: 'bao',   label: 'Bảo nhật',   luck: 'good', desc: `Chi sinh Can (${branchEl} sinh ${stemEl}) — ngày tốt.` };
  if (ELEMENT_OVERCOMES[stemEl] === branchEl)
    return { kind: 'che',   label: 'Chế nhật',   luck: 'medium', desc: `Can khắc Chi (${stemEl} khắc ${branchEl}) — ngày cát trung bình.` };
  if (ELEMENT_OVERCOMES[branchEl] === stemEl)
    return { kind: 'phat',  label: 'Phạt nhật',  luck: 'bad',  desc: `Chi khắc Can (${branchEl} khắc ${stemEl}) — ngày xấu.` };
  return null;
}

/** Convert a Chinese GanZhi pair like "甲子" to Vietnamese "Giáp Tý". */
function ganZhiToVi(gz) {
  if (!gz || gz.length < 2) return gz || '';
  const stem = HEAVENLY_STEMS[gz[0]] || gz[0];
  const branch = EARTHLY_BRANCHES[gz[1]] || gz[1];
  return `${stem} ${branch}`;
}

/** Convert each branch character in a string to its Sino-Vietnamese name. */
function branchesToVi(s) {
  if (!s) return '';
  return s.replace(/[子丑寅卯辰巳午未申酉戌亥]/g, ch => EARTHLY_BRANCHES[ch] || ch);
}

/** Convert a Chinese direction string to Vietnamese. */
function directionToVi(s) {
  if (!s) return '';
  return DIRECTIONS[s] || s;
}

/**
 * Full astrology read-out for a solar date — Hoàng Đạo / Hắc Đạo, can-chi day/
 * month/year, ngũ hành nạp âm, việc nên làm & nên tránh, giờ hoàng đạo, hướng
 * xuất hành tốt, Bành Tổ bách kỵ, tuổi xung.
 *
 * Built on top of lunar-javascript (already a project dependency). Returns a
 * plain object the UI can iterate over; Chinese-character outputs from the lib
 * are romanized into Sino-Vietnamese where a 1:1 mapping is unambiguous.
 *
 * @param {Date} solarDate
 * @returns {object|null}
 */
export function getDayAstrology(solarDate) {
  try {
    if (!(solarDate instanceof Date) || isNaN(solarDate.getTime())) return null;
    const solar = Solar.fromYmd(solarDate.getFullYear(), solarDate.getMonth() + 1, solarDate.getDate());
    const lunar = solar.getLunar();

    // 黄道 (yellow path / hoàng đạo) or 黑道 (black path / hắc đạo)
    const type = lunar.getDayTianShenType();
    const luck = lunar.getDayTianShenLuck();    // 吉 | 凶 — for secondary display
    const luckCode = type === '黄道' ? 'hoang_dao' : type === '黑道' ? 'hac_dao' : null;

    // Hour-by-hour classification — Hoàng Đạo (good) vs Hắc Đạo (bad).
    // 12 traditional two-hour blocks: Tý 23-1, Sửu 1-3, Dần 3-5, …, Hợi 21-23.
    const hourBlocks = [];
    const pad = (n) => String(n).padStart(2, '0');
    for (let i = 0; i < 12; i++) {
      const branchCh = '子丑寅卯辰巳午未申酉戌亥'[i];
      const branchVi = EARTHLY_BRANCHES[branchCh];
      const startHour = (i * 2 + 23) % 24;        // 23, 1, 3, 5, …
      const endHour = (startHour + 1 + 24) % 24;  // end of the 2-hour block (inclusive minute 59)
      // Sample the middle of the block to ask lunar-javascript for its type.
      const sampleHour = (startHour + 1) % 24;
      let blockType = null;
      try {
        const lunarAtHour = Lunar.fromYmdHms(
          solarDate.getFullYear(),
          solarDate.getMonth() + 1,
          solarDate.getDate(),
          sampleHour, 0, 0,
        );
        const t = lunarAtHour.getTimeTianShenType();
        blockType = t === '黄道' ? 'hoang_dao' : t === '黑道' ? 'hac_dao' : null;
      } catch (_) {}
      hourBlocks.push({
        branch: branchVi,
        range: `${pad(startHour)}:00 - ${pad(endHour)}:59`,
        type: blockType,
      });
    }

    // Branch relationships keyed on the day's Earthly Branch (Sino-Vietnamese).
    // Source: standard Vietnamese feng shui lookup tables.
    const dayBranchCh = (lunar.getDayInGanZhi() || '')[1];
    const dayBranchVi = EARTHLY_BRANCHES[dayBranchCh] || null;

    // Lục hợp (six harmonies): pairs of branches.
    const LUC_HOP = {
      'Tý': 'Sửu', 'Sửu': 'Tý',
      'Dần': 'Hợi', 'Hợi': 'Dần',
      'Mão': 'Tuất', 'Tuất': 'Mão',
      'Thìn': 'Dậu', 'Dậu': 'Thìn',
      'Tỵ': 'Thân', 'Thân': 'Tỵ',
      'Ngọ': 'Mùi', 'Mùi': 'Ngọ',
    };
    // Tam hợp (three harmonies): groups of three.
    const TAM_HOP_GROUPS = [
      ['Thân', 'Tý',  'Thìn'],  // Water
      ['Tỵ',   'Dậu', 'Sửu'],   // Metal
      ['Dần',  'Ngọ', 'Tuất'],  // Fire
      ['Hợi',  'Mão', 'Mùi'],   // Wood
    ];
    // Xung (six conflicts): opposing branches.
    const XUNG = {
      'Tý': 'Ngọ', 'Ngọ': 'Tý',
      'Sửu': 'Mùi','Mùi': 'Sửu',
      'Dần': 'Thân','Thân': 'Dần',
      'Mão': 'Dậu','Dậu': 'Mão',
      'Thìn':'Tuất','Tuất':'Thìn',
      'Tỵ': 'Hợi', 'Hợi': 'Tỵ',
    };
    const tamHopGroup = dayBranchVi
      ? (TAM_HOP_GROUPS.find(g => g.includes(dayBranchVi)) || [])
        .filter(b => b !== dayBranchVi)
      : [];
    const branchRelations = dayBranchVi ? {
      branch: dayBranchVi,
      lucHop: LUC_HOP[dayBranchVi] || null,
      tamHop: tamHopGroup,
      xung: XUNG[dayBranchVi] || null,
    } : null;

    // Tam Nương — six bad lunar days every month per ancient Chinese almanac.
    const TAM_NUONG_DAYS = [3, 7, 13, 18, 22, 27];
    const lunarDay = lunar.getDay();
    const isTamNuong = TAM_NUONG_DAYS.includes(lunarDay);

    // Nguyệt Kỵ — three "monthly avoidance" days: 5, 14, 23 of every lunar month.
    const NGUYET_KY_DAYS = [5, 14, 23];
    const isNguyetKy = NGUYET_KY_DAYS.includes(lunarDay);

    // Thập Nhị Kiến Trừ (Trực) — 12-phase building cycle for the day.
    const zhixingCh = lunar.getZhiXing?.() || '';
    const trucInfo = ZHI_XING[zhixingCh] || null;

    // Khổng Minh Lục Diệu — 6-day rotation. Day 1 → index 0 (Đại An).
    const lucDieu = LUC_DIEU[(lunarDay - 1) % 6];

    // Nhị Thập Bát Tú — 28 mansion. Name is translated to Sino-Vietnamese.
    // We replace the classical Chinese verse (xiuSong) with a Vietnamese
    // one-line description of the mansion's general nature, since translating
    // classical poetry on the fly isn't feasible.
    const xiuCh = lunar.getXiu?.() || null;
    const xiu = xiuCh ? (XIU_VI[xiuCh] ? `${XIU_VI[xiuCh]} (${xiuCh})` : xiuCh) : null;
    const xiuDesc = xiuCh ? (XIU_DESC_VI[xiuCh] || null) : null;

    // Ngũ Hành — Can-Chi element relationship narrative.
    const dayGz = lunar.getDayInGanZhi?.() || '';
    const dayStemEl = STEM_ELEMENT[dayGz[0]] || null;
    const dayBranchEl = BRANCH_ELEMENT[dayGz[1]] || null;
    const canChi = canChiRelation(dayStemEl, dayBranchEl);

    return {
      luck: luckCode,
      luckLabel: luckCode === 'hoang_dao' ? 'Hoàng Đạo' : luckCode === 'hac_dao' ? 'Hắc Đạo' : null,
      tianShen: lunar.getDayTianShen() || null,  // The actual god name (青龙, 天刑, ...)

      // Heavenly Stem + Earthly Branch (Can-Chi) of day / month / year
      dayGanZhi: ganZhiToVi(lunar.getDayInGanZhi()),
      monthGanZhi: ganZhiToVi(lunar.getMonthInGanZhi()),
      yearGanZhi: ganZhiToVi(lunar.getYearInGanZhi()),

      // 24 solar terms — name in Chinese; we leave it as the lib returns
      jieQi: lunar.getJieQi() || null,
      currentJieQi: lunar.getCurrentJieQi?.()?.getName?.() || null,

      // Five-element "nạp âm" — translated to Sino-Vietnamese.
      naYin: translate(lunar.getDayNaYin() || '', NA_YIN_VI),

      // Zodiac conflict — convert Chinese animal char (e.g. 马) to Vietnamese (Ngọ).
      chongShengXiao: translate(lunar.getDayChongShengXiao() || '', ZODIAC_ANIMALS_VI),
      chong: branchesToVi(lunar.getDayChong() || ''),
      sha: lunar.getDaySha() || null,

      // Activities recommended (Yi) and to avoid (Ji) — translated to Vietnamese
      // where we have a mapping; falls back to original Chinese for unknown items.
      yi: (lunar.getDayYi() || []).map(it => translate(it, YI_JI_VI)),
      ji: (lunar.getDayJi() || []).map(it => translate(it, YI_JI_VI)),

      // Auspicious / inauspicious stars — translated to Vietnamese.
      jiShen:   (lunar.getDayJiShen()   || []).map(s => translate(s, STAR_VI)),
      xiongSha: (lunar.getDayXiongSha() || []).map(s => translate(s, STAR_VI)),

      // Auspicious directions
      positions: {
        xi:      directionToVi(lunar.getDayPositionXiDesc?.()      || lunar.getDayPositionXi()),       // Hỷ thần (joy god)
        fu:      directionToVi(lunar.getDayPositionFuDesc?.()      || lunar.getDayPositionFu()),       // Phúc thần (fortune god)
        cai:     directionToVi(lunar.getDayPositionCaiDesc?.()     || lunar.getDayPositionCai()),      // Tài thần (wealth god)
        yangGui: directionToVi(lunar.getDayPositionYangGuiDesc?.() || lunar.getDayPositionYangGui()),  // Dương quý nhân
        yinGui:  directionToVi(lunar.getDayPositionYinGuiDesc?.()  || lunar.getDayPositionYinGui()),   // Âm quý nhân
      },

      // Pengzu's hundred taboos — folk almanac warnings tied to day stem/branch.
      // Translated to Sino-Vietnamese + a brief gloss; falls back to Chinese.
      pengZuGan: translate(lunar.getPengZuGan?.() || '', PENG_ZU_VI),
      pengZuZhi: translate(lunar.getPengZuZhi?.() || '', PENG_ZU_VI),

      // Tai Shen (fetus god) — relevant if there's a pregnant woman in the home
      taiShen: lunar.getDayPositionTai() || '',

      // All 12 two-hour blocks with their luck classification — the modal
      // splits them into "Giờ Hoàng Đạo" and "Giờ Hắc Đạo" columns.
      hourBlocks,
      // Day branch's relationships with other zodiac branches.
      branchRelations,
      // "Avoidance days" — Tam Nương (3, 7, 13, 18, 22, 27) and Nguyệt Kỵ
      // (5, 14, 23) per lunar month.
      isTamNuong,
      isNguyetKy,
      lunarDayOfMonth: lunarDay,
      // Thập Nhị Kiến Trừ — 12-phase building cycle.
      truc: trucInfo ? { ...trucInfo, ch: zhixingCh } : null,
      // Khổng Minh Lục Diệu — 6-day rotation by lunar day.
      lucDieu,
      // Nhị Thập Bát Tú — Vietnamese name + brief description (replaces verse).
      xiu,
      xiuDesc,
      // Ngũ Hành Can-Chi relationship (e.g. Hỏa khắc Kim → Chế nhật).
      dayStemElement: dayStemEl,
      dayBranchElement: dayBranchEl,
      canChi,
    };
  } catch (e) {
    console.error('getDayAstrology failed', e);
    return null;
  }
}
